import React from "react";
import { Text, View, Image } from "@react-pdf/renderer";
import { pdfStyles } from "./PDFStyles";

// Status Badge Component
export const StatusBadge = ({ status }) => {
  const getStatusStyle = (status) => {
    switch (status?.toLowerCase()) {
      case "paid":
      case "active":
        return pdfStyles.statusPaid;
      case "pending":
        return pdfStyles.statusPending;
      case "overdue":
        return pdfStyles.statusOverdue;
      default:
        return pdfStyles.statusPending;
    }
  };

  return (
    <Text style={[pdfStyles.statusBadge, getStatusStyle(status)]}>
      {status || "Unknown"}
    </Text>
  );
};

// Header Component
export const InvoiceHeader = ({
  displayHeaderSettings,
  invoice,
  formatDate,
  getInvoiceType,
  calculateSubtotal,
  calculateTax,
  calculateTotal,
  formatCurrency,
}) => (
  <View style={pdfStyles.header}>
    <View style={pdfStyles.headerContent}>
      {/* Company Section */}
      <View style={pdfStyles.companySection}>
        {displayHeaderSettings.logoPreview && (
          <Image
            src={displayHeaderSettings.logoPreview}
            style={pdfStyles.companyLogo}
          />
        )}
        <Text style={pdfStyles.companyName}>
          {displayHeaderSettings.companyName || "Your Company Name"}
        </Text>
        <View style={pdfStyles.companyDetails}>
          {displayHeaderSettings.companyAddress && (
            <Text style={pdfStyles.companyDetailItem}>
              {displayHeaderSettings.companyAddress}
            </Text>
          )}
          {displayHeaderSettings.companyPhone && (
            <Text style={pdfStyles.companyDetailItem}>
              Phone: {displayHeaderSettings.companyPhone}
            </Text>
          )}
          {displayHeaderSettings.companyEmail && (
            <Text style={pdfStyles.companyDetailItem}>
              Email: {displayHeaderSettings.companyEmail}
            </Text>
          )}
          {displayHeaderSettings.companyWebsite && (
            <Text style={pdfStyles.companyDetailItem}>
              Website: {displayHeaderSettings.companyWebsite}
            </Text>
          )}
          {displayHeaderSettings.taxNumber && (
            <Text style={pdfStyles.companyDetailItem}>
              Tax No: {displayHeaderSettings.taxNumber}
            </Text>
          )}
        </View>
      </View>

      {/* Invoice Section */}
      <View style={pdfStyles.invoiceSection}>
        <Text style={pdfStyles.invoiceTitle}>INVOICE</Text>
        <View style={pdfStyles.invoiceMeta}>
          <View style={pdfStyles.metaItem}>
            <Text style={pdfStyles.metaLabel}>Invoice Number:</Text>
            <Text style={pdfStyles.metaValue}>
              {invoice.invoiceNumber || "N/A"}
            </Text>
          </View>
          <View style={pdfStyles.metaItem}>
            <Text style={pdfStyles.metaLabel}>Invoice Type:</Text>
            <Text style={pdfStyles.metaValue}>{getInvoiceType()}</Text>
          </View>
          <View style={pdfStyles.metaItem}>
            <Text style={pdfStyles.metaLabel}>Invoice Date:</Text>
            <Text style={pdfStyles.metaValue}>
              {formatDate(invoice.createdAt || new Date())}
            </Text>
          </View>
          <View style={pdfStyles.metaItem}>
            <Text style={pdfStyles.metaLabel}>Due Date:</Text>
            <Text style={pdfStyles.metaValue}>
              {formatDate(
                invoice.dueDate || invoice.validUntil || invoice.nextDue,
              )}
            </Text>
          </View>

          <View style={pdfStyles.metaItem}>
            <Text style={pdfStyles.metaLabel}>Total Amount:</Text>
            <Text
              style={[
                pdfStyles.metaValue,
                { color: "#f69e22", fontWeight: "bold" },
              ]}
            >
              {formatCurrency(calculateTotal())}
            </Text>
          </View>
        </View>
      </View>
    </View>
  </View>
);

// Billing Section Component
export const BillingSection = ({
  invoice,
  facility,
  displayFooterSettings,
  getInvoiceType,
}) => (
  <View style={pdfStyles.billingSection}>
    {/* Bill To */}
    <View style={pdfStyles.billToSection}>
      <Text style={pdfStyles.sectionTitle}>Bill To</Text>
      <View style={pdfStyles.infoCard}>
        <Text style={pdfStyles.facilityName}>
          {invoice.facilityName || facility?.name || "Facility Name"}
        </Text>
        {facility?.location && (
          <Text style={pdfStyles.infoItem}>Location: {facility.location}</Text>
        )}
        {facility?.address && (
          <Text style={pdfStyles.infoItem}>Address: {facility.address}</Text>
        )}
        {facility?.contactPerson && (
          <Text style={pdfStyles.infoItem}>
            Contact: {facility.contactPerson}
          </Text>
        )}
        {facility?.email && (
          <Text style={pdfStyles.infoItem}>Email: {facility.email}</Text>
        )}
        {facility?.phone && (
          <Text style={pdfStyles.infoItem}>Phone: {facility.phone}</Text>
        )}
      </View>
    </View>

    {/* Payment Terms */}
    <View style={pdfStyles.paymentTermsSection}>
      <Text style={pdfStyles.sectionTitle}>Payment Terms</Text>
      <View style={pdfStyles.infoCard}>
        <View style={pdfStyles.infoItem}>
          <Text style={pdfStyles.infoLabel}>Payment Terms:</Text>
          <Text style={pdfStyles.infoValue}>
            {displayFooterSettings.paymentTerms || "Net 30 days"}
          </Text>
        </View>
        {displayFooterSettings.latePaymentFee && (
          <View style={pdfStyles.infoItem}>
            <Text style={pdfStyles.infoLabel}>Late Payment Fee:</Text>
            <Text style={pdfStyles.infoValue}>
              {displayFooterSettings.latePaymentFee}%
            </Text>
          </View>
        )}
        {displayFooterSettings.taxRate && (
          <View style={pdfStyles.infoItem}>
            <Text style={pdfStyles.infoLabel}>Tax Rate:</Text>
            <Text style={pdfStyles.infoValue}>
              {displayFooterSettings.taxRate}%
            </Text>
          </View>
        )}
        <View style={pdfStyles.infoItem}>
          <Text style={pdfStyles.infoLabel}>Invoice Type:</Text>
          <Text style={pdfStyles.infoValue}>{getInvoiceType()}</Text>
        </View>
      </View>
    </View>
  </View>
);

// Items Table Component
export const ItemsTable = ({ invoice, formatCurrency }) => (
  <View style={pdfStyles.itemsSection}>
    <View style={pdfStyles.table}>
      {/* Table Header */}
      <View style={pdfStyles.tableHeader}>
        <Text style={pdfStyles.descriptionCol}>Description</Text>
        <Text style={pdfStyles.quantityCol}>Qty</Text>
        <Text style={pdfStyles.priceCol}>Unit Price</Text>
        <Text style={pdfStyles.totalCol}>Total</Text>
      </View>

      {/* Table Rows */}
      {invoice.items && Array.isArray(invoice.items) ? (
        invoice.items.map((item, index) => (
          <View
            key={index}
            style={[
              pdfStyles.tableRow,
              index % 2 === 0 ? pdfStyles.tableRowEven : {},
            ]}
          >
            <View style={pdfStyles.descriptionCol}>
              <Text style={pdfStyles.itemDescription}>
                {item.description || item.name || "N/A"}
              </Text>
              {item.detail && (
                <Text style={pdfStyles.itemDetail}>{item.detail}</Text>
              )}
            </View>
            <Text style={pdfStyles.quantityCol}>{item.quantity || 1}</Text>
            <Text style={pdfStyles.priceCol}>
              {formatCurrency(item.unitPrice || item.amount || 0)}
            </Text>
            <Text style={pdfStyles.totalCol}>
              {formatCurrency(item.amount || 0)}
            </Text>
          </View>
        ))
      ) : (
        <View style={pdfStyles.tableRow}>
          <View style={pdfStyles.descriptionCol}>
            <Text style={pdfStyles.itemDescription}>
              {invoice.description || invoice.licenseType || "Service Charge"}
            </Text>
            {invoice.unitCount && (
              <Text style={pdfStyles.itemDetail}>
                @ KES {invoice.ratePerUnit?.toLocaleString() || 0} per unit
              </Text>
            )}
          </View>
          <Text style={pdfStyles.quantityCol}>{invoice.unitCount || 1}</Text>
          <Text style={pdfStyles.priceCol}>
            {formatCurrency(
              invoice.ratePerUnit ||
                invoice.monthlyRate ||
                invoice.amount ||
                invoice.totalAmount ||
                0,
            )}
          </Text>
          <Text style={pdfStyles.totalCol}>
            {formatCurrency(invoice.amount || invoice.totalAmount || 0)}
          </Text>
        </View>
      )}
    </View>
  </View>
);

// Totals Section Component
export const TotalsSection = ({
  calculateSubtotal,
  calculateTax,
  calculateTotal,
  displayFooterSettings,
  formatCurrency,
  invoice,
}) => {
  const subtotal = calculateSubtotal();
  const taxAmount = calculateTax();
  const total = calculateTotal();
  const taxRate = displayFooterSettings.taxRate || invoice?.taxRate;

  return (
    <View style={pdfStyles.totalsSection}>
      <View style={pdfStyles.totalsTable}>
        <View style={pdfStyles.totalsRow}>
          <Text>Subtotal</Text>
          <Text style={pdfStyles.textRight}>{formatCurrency(subtotal)}</Text>
        </View>
        {taxRate && taxAmount > 0 && (
          <View style={pdfStyles.totalsRow}>
            <Text>Tax ({taxRate}%)</Text>
            <Text style={pdfStyles.textRight}>{formatCurrency(taxAmount)}</Text>
          </View>
        )}
        <View style={pdfStyles.totalsFinalRow}>
          <Text>Total Amount</Text>
          <Text style={pdfStyles.textRight}>{formatCurrency(total)}</Text>
        </View>
      </View>
    </View>
  );
};

// Footer Component
export const InvoiceFooter = ({
  displayHeaderSettings,
  displayFooterSettings,
}) => (
  <View style={pdfStyles.footer}>
    <View style={pdfStyles.footerContent}>
      {/* Payment Information */}
      <View style={pdfStyles.footerSection}>
        <Text style={pdfStyles.footerTitle}>Payment Information</Text>
        <View style={pdfStyles.footerCard}>
          {displayFooterSettings.bankName && (
            <View style={pdfStyles.infoItem}>
              <Text style={pdfStyles.infoLabel}>Bank Name:</Text>
              <Text style={pdfStyles.infoValue}>
                {displayFooterSettings.bankName}
              </Text>
            </View>
          )}
          {displayFooterSettings.accountName && (
            <View style={pdfStyles.infoItem}>
              <Text style={pdfStyles.infoLabel}>Account Name:</Text>
              <Text style={pdfStyles.infoValue}>
                {displayFooterSettings.accountName}
              </Text>
            </View>
          )}
          {displayFooterSettings.accountNumber && (
            <View style={pdfStyles.infoItem}>
              <Text style={pdfStyles.infoLabel}>Account Number:</Text>
              <Text style={pdfStyles.infoValue}>
                {displayFooterSettings.accountNumber}
              </Text>
            </View>
          )}
          {displayFooterSettings.swiftCode && (
            <View style={pdfStyles.infoItem}>
              <Text style={pdfStyles.infoLabel}>SWIFT Code:</Text>
              <Text style={pdfStyles.infoValue}>
                {displayFooterSettings.swiftCode}
              </Text>
            </View>
          )}
        </View>
      </View>

      {/* Support Information */}
      <View style={pdfStyles.footerSection}>
        <Text style={pdfStyles.footerTitle}>Support Information</Text>
        <View style={pdfStyles.footerCard}>
          {displayFooterSettings.supportEmail && (
            <View style={pdfStyles.infoItem}>
              <Text style={pdfStyles.infoLabel}>Support Email:</Text>
              <Text style={pdfStyles.infoValue}>
                {displayFooterSettings.supportEmail}
              </Text>
            </View>
          )}
          {displayFooterSettings.supportPhone && (
            <View style={pdfStyles.infoItem}>
              <Text style={pdfStyles.infoLabel}>Support Phone:</Text>
              <Text style={pdfStyles.infoValue}>
                {displayFooterSettings.supportPhone}
              </Text>
            </View>
          )}
          {!displayFooterSettings.supportEmail &&
            !displayFooterSettings.supportPhone && (
              <View style={pdfStyles.infoItem}>
                <Text style={pdfStyles.infoLabel}>Email:</Text>
                <Text style={pdfStyles.infoValue}>
                  {displayHeaderSettings.companyEmail || "N/A"}
                </Text>
              </View>
            )}
        </View>
      </View>
    </View>

    {/* Thank You Message */}
    <View style={pdfStyles.thankYou}>
      <Text style={pdfStyles.thankYouText}>
        Thank you for your business! We value your partnership.
      </Text>
    </View>
  </View>
);
